/* eslint-disable @nx/enforce-module-boundaries */
import { forwardRef, useMemo, useState } from 'react';
import { SupportLayout } from './layout';
import {
  Box,
  Card,
  Divider,
  HStack,
  Heading,
  IconButton,
  Link,
  List,
  ListItem,
  Stack,
  Text,
  useToast,
} from '@mybridge/ui';
import { BackArrow, BackArrowRTL, ChevronLeft, ChevronRight } from '@mybridge/icons';
import _ from 'lodash';
import { SupportForm } from './form';
import { useMutation } from '@tanstack/react-query';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getUserFullName } from 'v4/lib/commons';
import { createSupportTicket } from 'v4/store/actions/support.actions';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useRouter } from 'next/router';

export const Item = forwardRef(
  ({ title, leftIcon, rightIcon, ...props }, ref) => {
    return (
      <HStack _hover={{ bg: 'gray.50' }} p={4} {...props}>
        {leftIcon}
        <Text flex={1}>{title}</Text>
        {rightIcon}
      </HStack>
    );
  }
);
//TODO @ ketan: Ketan needs to implement this options from backend.
export const SupportIndex = forwardRef(({ ...props }, ref) => {
  const { t } = useTranslator();
  const direction = useDirection();
  const router = useRouter();
  const items = useMemo(
    () => [
      {
        title: t('otherModules.accountSecurity') || 'Account & Security',
        items: [
          {
            title: t('otherModules.accountIssues') || 'Account issues',
            hasForm: true,
            category: "Problem"
          },
          {
            title: t('otherModules.termsOfService') || 'Terms of service',
            hasForm: true,
            category: "Question"
          },
          {
            title: t('otherModules.reportBug') || 'Report a bug',
            hasForm: true,
            category: "Incident"
          },
        ],
      },
      {
        title: t('otherModules.featureRequest') || 'Feature Request',
        items: [
          {
            title: t('otherModules.requestNewFeature') || 'Request a new feature',
            hasForm: true,
            category:"Feature Request"
          },
        ],
      },
      {
        title: t('otherModules.subscriptionsBilling') || 'Subscriptions, Billing & Payments',
        items: [
          {
            title: t('otherModules.paymentsBilling') || 'Payments & Billing',
            hasForm: true,
            category:"Incident"
          },
          {
            title: t('otherModules.refund') || 'Refund',
            hasForm: true,
            category:"Refund"
          },
          {
            title: t('otherModules.subscriptions') || 'Subscriptions',
            hasForm: true,
            category:"Incident"
          },
          {
            title: t('otherModules.bugs') || 'Bugs',
            hasForm: true,
            category:"Problem"
          },
        ],
      },
      {
        title: 'Feedback Form',
        link: 'https://forms.gle/BYZU66c2rNptvedH8',
      },
    ],
    []
  );

  const { userProfileInfo } = useSelector((s) => s.userProfile);
  const [history, setHistory] = useState([]);
  const [done, setDone] = useState(false);
  const dispatch = useDispatch();
  const { loading } = useSelector((s) => s.supportReducer);

  const handleItemClick = (ind) => (e) => {
    const item = cItems?.[ind];
    if (item?.link) {
      window.open(item.link, '_blank');
      return;
    }
    history.push(ind);
    setHistory([...history]);
    setDone(false);
  };
  const handleBackClick = (e) => {
    history.pop();
    setHistory([...history]);
  };

  const getItems = (arr = [], his = []) => {
    for (let i of his) {
      const [_, ...nh] = his;
      return getItems(arr[_ ?? 0]?.items, nh);
    }
    return arr;
  };

  const getItem = (arr = [], his = []) => {
    for (let i of his) {
      const [_, ...nh] = his;
      return !arr[_ ?? 0]?.items || arr[_ ?? 0]?.items?.length <= 0
        ? arr[_ ?? 0]
        : getItem(arr[_ ?? 0]?.items, nh);
    }
    return;
  };

  const goTo = (ind) => {
    const his_ = history.splice(0, ind);
    setHistory([...his_]);
  };

  const getItemTitles = (arr = [], his = [], title = '') => {
    for (let i of his) {
      const [_, ...nh] = his;
      return getItemTitles(
        arr[_ ?? 0]?.items,
        nh,
        <>
          {title ? (
            <>
              <Text
                flexShrink={0}
                as={Link}
                onClick={(e) => {
                  goTo(his.length);
                }}
              >
                {title}
              </Text>
              <Box flexShrink={0}>
                {direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
              </Box>
            </>
          ) : (
            ''
          )}
          <Text flexShrink={0} as={Link} onClick={(e) => {}}>
            {arr[_ ?? 0]?.title}
          </Text>
        </>
      );
    }
    return title;
  };

  const cItems = useMemo(() => getItems(items, history), [items, history]);
  const selected = useMemo(() => getItem(items, history), [items, history]);
  const toast = useToast({ position: 'top', isClosable: true });
  const historyView = ({ arr, his }) => {
    return (
      <HStack overflowX="auto">
        <Text flexShrink={0} as={Link} onClick={(e) => setHistory([])}>
          {t('otherModules.home_support') || "Home & Support"}
        </Text>
        <Box flexShrink={0}>
          {direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
        </Box>
        {getItemTitles(arr, his)}
      </HStack>
    );
  };

  const handleSubmit = async (form) => {
    try {
      const fd = new FormData();
      fd.append('subject', form?.title);
      fd.append('description', form?.message);
      fd.append('email', form?.from ?? userProfileInfo?.email);
      fd.append('name', form?.name ?? getUserFullName(userProfileInfo));
      fd.append('category', selected?.category);
      fd.append('tags', selected?.title)
      // fd.append('actAsType', 'customer');
      if (form?.attachments) {
        fd.append('attachments', form?.attachments);
      }

      const status = await dispatch(createSupportTicket(fd));
      // console.log(status);
      if (status) {
        toast({
          title: t('otherModules.thanks_for_sharing_issue') || 'Thanks for sharing your issue',
          description: t('otherModules.response_time_notice') || 'We will get back to you as soon as possible',
          status: 'success',
        });
        setDone(true);
      } else {
        toast({ title: 'Error', description: status.detail, status: 'error' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.message, status: 'error' });
    }
  };

  // console.log(selected);

  return (
    <>
      <SupportLayout>
        <Stack spacing={0} gap={8} pr={[0, 0, 24]}>
          {history.length ? (
            <>{historyView({ arr: items, his: history })}</>
          ) : (
            ''
          )}
          {selected?.hasForm ? (
            ''
          ) : (
            <HStack><IconButton size="sm" variant="transparent" onClick={() => router.back()} icon={direction === 'rtl' ? <BackArrowRTL/> : <BackArrow />} /><Heading size="md">{t('otherModules.help_prompt') || "Hello, how can we help you?"}</Heading></HStack>
          )}

          <Card>
            <Stack spacing={0}>
              {/* {history.length > 0 ? (
                <>
                  <Item
                    cursor="pointer"
                    onClick={handleBackClick}
                    title="Back"
                    leftIcon={<ChevronLeft />}
                  />
                  <Divider />
                </>
              ) : (
                ''
              )} */}
              {cItems?.map?.((i, ii) => (
                <Stack key={ii} cursor="pointer" spacing={0}>
                  {ii > 0 ? <Divider /> : ''}
                  <Item
                    onClick={handleItemClick(ii)}
                    rightIcon={direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
                    {...(i ?? {})}
                  />
                </Stack>
              ))}
            </Stack>
          </Card>
        </Stack>
        {selected?.hasForm ? (
          done ? (
            <>
              <Card p={4} as={Stack} alignItems="center">
                <HStack justifyContent="center">
                  <CheckCircleIcon color="green.400" w="12" h="12" />
                </HStack>
                <Heading>{t('otherModules.issue_submitted') || "Issue submitted"}</Heading>
                <Text textAlign="center">
                  {t('otherModules.issue_submission_thank_you') || "Thank you for sharing your issue with us. We will get back to you as soon as possible!"}
                </Text>
              </Card>
            </>
          ) : (
            <SupportForm
              isLoading={loading}
              onSubmit={handleSubmit}
              onBackClick={handleBackClick}
            />
          )
        ) : (
          ''
        )}
      </SupportLayout>
    </>
  );
});
